import type { EventObject, MetaObject, SetupTypes, SnapshotFrom } from "xstate";
import { assign, fromPromise, setup } from "xstate";

export const E_OnboardingV2CompletionOutcome = {
  BrokerageConnected: "brokerage_connected",
  BrokerageSkipped: "brokerage_skipped",
  HoldingsSubmitted: "holdings_submitted",
} as const;

export type T_OnboardingV2CompletionOutcome =
  (typeof E_OnboardingV2CompletionOutcome)[keyof typeof E_OnboardingV2CompletionOutcome];

export type T_OnboardingV2CompletionMachineInput = {
  complete: (outcome: T_OnboardingV2CompletionOutcome) => Promise<void>;
  onCompleted: (outcome: T_OnboardingV2CompletionOutcome) => void;
};

type T_OnboardingV2CompletionContext = {
  error: string | null;
  input: T_OnboardingV2CompletionMachineInput;
  outcome: T_OnboardingV2CompletionOutcome | null;
};

type T_OnboardingV2CompletionEvent =
  | { outcome: T_OnboardingV2CompletionOutcome; type: "BEGIN" }
  | { type: "RETRY" };

type T_CompleteActorInput = {
  complete: T_OnboardingV2CompletionMachineInput["complete"];
  outcome: T_OnboardingV2CompletionOutcome | null;
};

const onboardingV2CompletionSetupTypes: SetupTypes<
  T_OnboardingV2CompletionContext,
  T_OnboardingV2CompletionEvent,
  Record<never, string>,
  string,
  T_OnboardingV2CompletionMachineInput,
  unknown,
  EventObject,
  MetaObject
> = {};

function getCompletionErrorMessage(error: unknown): string {
  return error instanceof Error
    ? error.message
    : "Lattice could not finish onboarding. Please try again.";
}

export type T_OnboardingV2CompletionStatus = "completing" | "failed" | null;

export const onboardingV2CompletionMachine = setup({
  types: onboardingV2CompletionSetupTypes,
  actors: {
    complete: fromPromise<undefined, T_CompleteActorInput>(
      async ({ input }) => {
        if (input.outcome === null) {
          throw new Error("The onboarding completion outcome is missing.");
        }
        await input.complete(input.outcome);
        return undefined;
      },
    ),
  },
  actions: {
    notifyCompleted: ({ context }) => {
      if (context.outcome !== null) {
        context.input.onCompleted(context.outcome);
      }
    },
    selectOutcome: assign({
      error: null,
      outcome: ({ event }) => (event.type === "BEGIN" ? event.outcome : null),
    }),
    setError: assign({
      error: (_, params: { error: unknown }) =>
        getCompletionErrorMessage(params.error),
    }),
  },
}).createMachine({
  id: "onboardingV2Completion",
  context: ({ input }) => ({
    error: null,
    input,
    outcome: null,
  }),
  initial: "idle",
  states: {
    idle: {
      on: {
        BEGIN: {
          actions: "selectOutcome",
          target: "completing",
        },
      },
    },
    completing: {
      invoke: {
        src: "complete",
        input: ({ context }) => ({
          complete: context.input.complete,
          outcome: context.outcome,
        }),
        onDone: {
          actions: "notifyCompleted",
          target: "completed",
        },
        onError: {
          actions: {
            type: "setError",
            params: ({ event }) => ({ error: event.error }),
          },
          target: "failed",
        },
      },
    },
    failed: {
      on: {
        RETRY: {
          actions: assign({ error: null }),
          target: "completing",
        },
      },
    },
    completed: { type: "final" },
  },
});

export function projectOnboardingV2CompletionStatus(
  snapshot: SnapshotFrom<typeof onboardingV2CompletionMachine>,
): T_OnboardingV2CompletionStatus {
  if (snapshot.matches("failed")) return "failed";
  if (snapshot.matches("completing") || snapshot.matches("completed")) {
    return "completing";
  }
  return null;
}
